import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { useRichTextState } from "@/hooks/useRichTextState.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { RichText } from "@/uiKit/molecules/RichText/RichText.tsx";

export type PatientNoteCardProps = {
  className?: string;
  patientId: string;
  classes?: {
    root?: string;
    header?: string;
    content?: string;
    footer?: string;
  };
};

export const PatientNoteCard: React.FC<PatientNoteCardProps> = ({ className, classes, patientId }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const { data: featureFlags } = Queries.user.useFeatureFlags({});
  const note = Queries.notes.useNote({
    patientId,
  });
  const writeNote = Queries.notes.useWriteNote();

  const richTextState = useRichTextState(note.content, {
    onSave: note => {
      writeNote.mutate(
        { patientId, note },
        {
          onSuccess: () => TextToast.success(t("pages.patientMedicalFile.noteSuccess")),
        },
      );
    },
  });

  if (!featureFlags?.["medical-team-notes"]) {
    return null;
  }

  return (
    <Card className={cx(styles.container, className, classes?.root)}>
      <CardHeader title={t("pages.patientMedicalFile.notes")} className={cx(styles.header, classes?.header)} />
      <CardContent className={styles.content}>
        <RichText
          idPrefix="team-note"
          richTextState={richTextState}
          createNewTextTitle={t("pages.patientMedicalFile.writeNote")}
          editTextTitle={t("pages.patientMedicalFile.editNote")}
        />
      </CardContent>
      <CardFooter />
    </Card>
  );
};

const makeStyles = () => ({
  container: css({
    minWidth: 0,
    maxHeight: "80vh",
  }),
  header: css({
    flex: "0",
  }),
  content: css({
    display: "flex",
    flexDirection: "column",
    overflow: "clip",
    minHeight: 0,
  }),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
